/**
 * Parses a UTC timestamp string and ensures it's interpreted as UTC
 * Fixes issue where timestamps without 'Z' are interpreted as local time
 */
function parseUTCTimestamp(timestamp: string): Date {
  const timestampStr = timestamp.trim();
  const hasTimezone = timestampStr.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(timestampStr);
  const utcString = hasTimezone ? timestampStr : `${timestampStr}Z`;
  return new Date(utcString);
}

/**
 * Formats a UTC timestamp to relative time string (e.g., "2 hours ago")
 */
export function formatRelativeTime(utcTimestamp: string | Date): string {
  try {
    const date = typeof utcTimestamp === 'string'
      ? parseUTCTimestamp(utcTimestamp)
      : utcTimestamp;

    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }

    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInWeeks = Math.floor(diffInDays / 7);
    const diffInMonths = Math.floor(diffInDays / 30);
    const diffInYears = Math.floor(diffInDays / 365);

    if (diffInMs < 0 || diffInSeconds < 60) {
      return 'Just now';
    }

    if (diffInMinutes < 60) {
      return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
    }

    if (diffInHours < 24) {
      return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
    }

    if (diffInDays < 7) {
      return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
    }

    if (diffInDays < 30) {
      return `${diffInWeeks} ${diffInWeeks === 1 ? 'week' : 'weeks'} ago`;
    }

    if (diffInDays < 365) {
      return `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`;
    }

    return `${diffInYears} ${diffInYears === 1 ? 'year' : 'years'} ago`;
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return 'Unknown time';
  }
}
